<lib-auth-layout>
  <div *transloco="let t">
    <div class="reset-container">
      <div class="reset-header">
        @if (currentStep() !== 'request') {
        <button
          type="button"
          (click)="goBack()"
          class="back-to-login"
          [attr.aria-label]="t('reset.header')"
        >
          <i class="fas fa-arrow-left"></i>
        </button>
        } @else {
        <a
          routerLink="/auth/login"
          class="back-to-login"
          title="{{ t('login.title') }}"
        >
          <i class="fas fa-arrow-left"></i>
        </a>
        }
        <h3>{{ t('reset.header') }}</h3>
      </div>

      <div class="reset-body">
        <!-- Success Message -->
        @if (successMessage()) {
        <div class="alert alert-success" role="alert">
          {{ successMessage() }}
        </div>
        }

        <!-- Error Message -->
        @if (errorMessage()) {
        <div class="alert alert-danger" role="alert">
          {{ errorMessage() }}
        </div>
        }

        <!-- Step 1: Request OTP -->
        @if (currentStep() === 'request') {
        <div class="step-content">
          <div class="step-icon">
            <i class="fas fa-envelope"></i>
          </div>
          <p class="step-instructions">{{ t('reset.step1_instructions') }}</p>

          <form [formGroup]="requestForm" (ngSubmit)="onRequestOtp()">
            <div class="mb-3 form-floating">
              <input
                type="email"
                class="form-control"
                [class.is-invalid]="
                  requestForm.get('email')?.invalid &&
                  requestForm.get('email')?.touched
                "
                id="email"
                formControlName="email"
                placeholder="{{ t('reset.email_placeholder') }}"
                autocomplete="email"
              />
              <label for="email">{{ t('reset.email_label') }}</label>
              @if (requestForm.get('email')?.invalid &&
              requestForm.get('email')?.touched) {
              <div class="invalid-feedback">
                @if (requestForm.get('email')?.errors?.['required']) {
                {{ t('validation.email_required') }}
                } @if (requestForm.get('email')?.errors?.['email']) {
                {{ t('validation.email_invalid') }}
                }
              </div>
              }
            </div>

            <button
              type="submit"
              class="btn btn-primary w-100"
              [disabled]="isLoading()"
            >
              @if (isLoading()) {
              <span
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t('common.sending') }}
              } @else {
              <i class="fas fa-paper-plane me-2"></i>
              {{ t('reset.send_code') }}
              }
            </button>
          </form>
        </div>
        }

        <!-- Step 2: Verify OTP -->
        @if (currentStep() === 'verify') {
        <div class="step-content">
          <div class="step-icon">
            <i class="fas fa-key"></i>
          </div>
          <p class="step-instructions">
            {{ t('reset.step2_instructions') }} <strong>{{ email() }}</strong>
          </p>

          <!-- Timer -->
          <div class="otp-timer" [class.expired]="otpExpirySeconds() === 0">
            <i class="fas fa-clock me-2"></i>
            {{ t('reset.otp_expires_in') }}
            <strong>{{ formattedTimer }}</strong>
          </div>

          <form [formGroup]="verifyForm" (ngSubmit)="onVerifyOtp()">
            <div class="mb-3">
              <input
                type="text"
                class="form-control otp-input"
                [class.is-invalid]="
                  verifyForm.get('otp')?.invalid &&
                  verifyForm.get('otp')?.touched
                "
                id="otp"
                formControlName="otp"
                [attr.placeholder]="t('reset.otp_placeholder')"
                maxlength="6"
                pattern="[0-9]*"
                inputmode="numeric"
                autocomplete="one-time-code"
              />
              @if (verifyForm.get('otp')?.invalid &&
              verifyForm.get('otp')?.touched) {
              <div class="invalid-feedback">
                {{ t('reset.otp_invalid') }}
              </div>
              }
            </div>

            <button
              type="submit"
              class="btn btn-primary w-100 mb-2"
              [disabled]="isLoading() || otpExpirySeconds() === 0"
            >
              @if (isLoading()) {
              <span
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t('common.verifying') }}
              } @else {
              <i class="fas fa-check me-2"></i>
              {{ t('reset.verify_button') }}
              }
            </button>

            <button
              type="button"
              class="btn btn-outline-secondary w-100"
              (click)="resendOtp()"
              [disabled]="isLoading()"
            >
              <i class="fas fa-redo me-2"></i>
              {{ t('reset.resend_button') }}
            </button>
          </form>
        </div>
        }

        <!-- Step 3: Reset Password -->
        @if (currentStep() === 'reset') {
        <div class="step-content">
          <div class="step-icon">
            <i class="fas fa-lock"></i>
          </div>
          <p class="step-instructions">{{ t('reset.step3_instructions') }}</p>

          <form [formGroup]="resetForm" (ngSubmit)="onResetPassword()">
            <!-- New Password -->
            <div class="mb-3">
              <div class="input-group">
                <div class="form-floating flex-grow-1">
                  <input
                    [type]="showPassword() ? 'text' : 'password'"
                    class="form-control"
                    [class.is-invalid]="
                      resetForm.get('newPassword')?.invalid &&
                      resetForm.get('newPassword')?.touched
                    "
                    id="newPassword"
                    formControlName="newPassword"
                    placeholder="{{ t('reset.new_password_placeholder') }}"
                    autocomplete="new-password"
                  />
                  <label for="newPassword">{{
                    t('reset.new_password_label')
                  }}</label>
                </div>
                <button
                  type="button"
                  class="input-group-text password-toggle"
                  (click)="togglePasswordVisibility('password')"
                  [attr.aria-label]="t('reset.new_password_label')"
                >
                  <i
                    [class]="showPassword() ? 'fas fa-eye-slash' : 'fas fa-eye'"
                  ></i>
                </button>
              </div>

              <!-- Password Strength Indicator -->
              @if (resetForm.get('newPassword')?.value &&
              resetForm.get('newPassword')!.value.length > 0) {
              <div class="password-strength mt-2">
                <div
                  class="password-strength-bar"
                  [class.weak]="!passwordIsStrong()"
                  [class.strong]="passwordIsStrong()"
                ></div>
              </div>

              <!-- Password Requirements -->
              <div class="password-requirements mt-2">
                <ul>
                  <li [class.met]="passwordRequirements().hasMinLength">
                    <i
                      [class]="
                        passwordRequirements().hasMinLength
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('reset.requirements.min') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasUppercase">
                    <i
                      [class]="
                        passwordRequirements().hasUppercase
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('reset.requirements.upper') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasLowercase">
                    <i
                      [class]="
                        passwordRequirements().hasLowercase
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('reset.requirements.lower') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasNumber">
                    <i
                      [class]="
                        passwordRequirements().hasNumber
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('reset.requirements.number') }}
                  </li>
                  <li [class.met]="passwordRequirements().hasSpecialChar">
                    <i
                      [class]="
                        passwordRequirements().hasSpecialChar
                          ? 'fas fa-check'
                          : 'fas fa-times'
                      "
                    ></i>
                    {{ t('reset.requirements.special') }}
                  </li>
                </ul>
              </div>
              }
            </div>

            <!-- Confirm Password -->
            <div class="mb-3">
              <div class="input-group">
                <div class="form-floating flex-grow-1">
                  <input
                    [type]="showConfirmPassword() ? 'text' : 'password'"
                    class="form-control"
                    [class.is-invalid]="
                      (resetForm.get('confirmPassword')?.invalid &&
                        resetForm.get('confirmPassword')?.touched) ||
                      (resetForm.get('confirmPassword')?.touched &&
                        !passwordsMatch)
                    "
                    id="confirmPassword"
                    formControlName="confirmPassword"
                    placeholder="{{ t('reset.confirm_password_placeholder') }}"
                    autocomplete="new-password"
                  />
                  <label for="confirmPassword">{{
                    t('reset.confirm_password_label')
                  }}</label>
                </div>
                <button
                  type="button"
                  class="input-group-text password-toggle"
                  (click)="togglePasswordVisibility('confirmPassword')"
                  [attr.aria-label]="t('reset.confirm_password_label')"
                >
                  <i
                    [class]="
                      showConfirmPassword() ? 'fas fa-eye-slash' : 'fas fa-eye'
                    "
                  ></i>
                </button>
              </div>
              @if (resetForm.get('confirmPassword')?.touched && !passwordsMatch)
              {
              <div class="invalid-feedback d-block">
                {{ t('reset.passwords_mismatch') }}
              </div>
              }
            </div>

            <button
              type="submit"
              class="btn btn-primary w-100"
              [disabled]="isLoading()"
            >
              @if (isLoading()) {
              <span
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t('common.resetting') }}
              } @else {
              <i class="fas fa-check me-2"></i>
              {{ t('reset.reset_button') }}
              }
            </button>
          </form>
        </div>
        }

        <!-- Login Link -->
        <div class="text-center mt-3">
          {{ t('register.login_prompt') }}
          <a routerLink="/auth/login">{{ t('register.login_link') }}</a>
        </div>
      </div>
    </div>
  </div>
</lib-auth-layout>
